import makeStyles from '@mui/styles/makeStyles'

export const useStyles = makeStyles(({ spacing, palette }) => ({
  container: {
    padding: 0
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing(4),
    gap: spacing(2)
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2)
  },
  viewToggle: {
    '& .MuiToggleButton-root': {
      padding: spacing(0.5),
      border: `1px solid ${palette.divider || '#e0e0e0'}`,
      color: palette.text?.secondary || '#666',
      '&.Mui-selected': {
        backgroundColor: palette.primary?.main || '#1976d2',
        color: 'white',
        '&:hover': {
          backgroundColor: palette.primary?.dark || '#1565c0'
        }
      },
      '&:hover': {
        backgroundColor: palette.action?.hover || 'rgba(0, 0, 0, 0.04)'
      }
    }
  },
  title: {
    fontWeight: 600,
    marginBottom: spacing(0.5)
  },
  subtitle: {
    fontSize: '0.875rem',
    lineHeight: 1.5
  },
  pagesGrid: {
    marginTop: spacing(2)
  },
  pageCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.2s ease-in-out',
    border: `1px solid ${palette.divider || '#e0e0e0'}`,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      borderColor: palette.primary?.main || '#1976d2'
    }
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(3)
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing(2)
  },
  pageIconContainer: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: palette.primary?.light || '#e3f2fd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  pageIcon: {
    fontSize: 20,
    color: palette.primary?.main || '#1976d2'
  },
  cardActions: {
    display: 'flex',
    gap: spacing(0.5)
  },
  actionButton: {
    color: palette.text?.secondary || '#666',
    '&:hover': {
      backgroundColor: palette.action?.hover || 'rgba(0, 0, 0, 0.04)',
      color: palette.primary?.main || '#1976d2'
    }
  },
  deleteButton: {
    color: palette.text?.secondary || '#666',
    '&:hover': {
      backgroundColor: palette.error?.light || '#ffebee',
      color: palette.error?.main || '#f44336'
    }
  },
  cardBody: {
    flex: 1,
    marginBottom: spacing(2)
  },
  pageName: {
    fontWeight: 600,
    marginBottom: spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1)
  },
  pageDescription: {
    fontSize: '0.75rem',
    lineHeight: 1.3,
    marginBottom: spacing(0.25),
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    maxWidth: '350px'
  },
  pageMetadata: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing(1)
  },
  defaultChip: {
    backgroundColor: palette.primary?.light || '#e3f2fd',
    color: palette.primary?.main || '#1976d2',
    fontSize: '0.70rem',
    height: 18,
    fontWeight: 500
  },
  publishedChip: {
    borderRadius: '4px',
    color: palette.common?.white || '#fff',
    fontSize: '0.75rem',
    '&:hover': { cursor: 'pointer' }
  },
  draftChip: {
    borderRadius: '4px',
    color: palette.common?.white || '#fff',
    fontSize: '0.75rem',
    '&:hover': { cursor: 'pointer' }
  },
  sectionsInfo: {
    marginTop: spacing(1)
  },
  cardFooter: {
    marginTop: 'auto'
  },
  // List view styles (Grid-based like skill bites)
  tablePaper: {
    marginTop: spacing(2),
    border: '1px solid rgba(0, 0, 0, 0.12)'
  },
  gridHeader: {
    height: 60,
    borderBottom: `1px solid ${palette.divider || '#e0e0e0'}`,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0.25
  },
  gridRow: {
    minHeight: 80,
    padding: spacing(1.5, 0),
    borderBottom: `1px solid ${palette.divider || '#e0e0e0'}`,
    position: 'relative',
    '&:hover': {
      backgroundColor: palette.background?.selected || 'rgba(0, 0, 0, 0.04)',
      cursor: 'pointer'
    }
  },
  defaultPageRow: {
    '&::before': {
      content: 'close-quote',
      position: 'absolute',
      left: 0,
      top: '35%',
      transform: 'translateY(-50%)',
      borderRadius: spacing(0, 2, 2, 0),
      width: spacing(0.75),
      height: spacing(6),
      backgroundColor: palette.primary?.main || '#1976d2'
    }
  },
  pageNameColumn: {
    padding: spacing(0, 2.5, 0, 3)
  },
  pageIconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '32px',
    width: '32px',
    marginRight: spacing(2),
    marginTop: spacing(0.25),
    padding: spacing(0.5),
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 4,
    flexShrink: 0
  },
  defaultPageIconWrapper: {
    backgroundColor: palette.primary?.light || '#e3f2fd'
  },
  regularPageIconWrapper: {
    backgroundColor: palette.grey?.[100] || '#f5f5f5'
  },
  listPageIcon: {
    fontSize: 18
  },
  defaultPageIcon: {
    color: palette.primary?.main || '#1976d2'
  },
  regularPageIcon: {
    color: palette.grey?.[500] || '#9e9e9e'
  },
  pageUrl: {
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.5)
  },
  linkIcon: {
    fontSize: '0.75rem',
    opacity: 0.6,
    color: palette.text?.secondary || '#666'
  },
  pathDot: {
    fontSize: '0.75rem',
    opacity: 0.6,
    color: palette.text?.secondary || '#666',
    marginLeft: spacing(0.25)
  },
  listActions: {
    display: 'flex',
    gap: spacing(0.5),
    justifyContent: 'flex-end',
    paddingRight: spacing(2),
    width: '100%'
  },
  pageTitle: {
    fontWeight: 500,
    paddingBottom: spacing(0.3)
  },
  gridItemCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80px'
  },
  gridItemLeft: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '80px'
  },
  gridItemRight: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '80px',
    justifyContent: 'flex-end'
  },
  headerItemLeft: {
    paddingLeft: spacing(3)
  },
  headerItemCenter: {
    textAlign: 'center'
  },
  headerItemRight: {
    textAlign: 'right',
    paddingRight: spacing(2)
  }
}))
